import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { ShieldCheck, Check, X, FileText, Megaphone, Clock } from "@phosphor-icons/react";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const ApprovalsCenter = ({ user, logout }) => {
  const [tab, setTab] = useState("billing");
  const [billing, setBilling] = useState([]);
  const [marketing, setMarketing] = useState([]);
  const [loading, setLoading] = useState(true);
  const role = user?.role || "";
  const canApprove = ["admin", "manager", "bdm", "marketing_head", "cfo"].includes(role);

  const load = useCallback(async () => {
    if (!canApprove) { setLoading(false); return; }
    try {
      setLoading(true);
      const [b, m] = await Promise.all([
        api.get(`/billing-approvals/pending`),
        axios.get(`${API}/mktg-campaigns?status=submitted`).then(r => r.data).catch(() => []),
      ]);
      const mgrApprov = await axios.get(`${API}/mktg-campaigns?status=manager_approved`).then(r => r.data).catch(() => []);
      const mktgAprov = await axios.get(`${API}/mktg-campaigns?status=marketing_head_approved`).then(r => r.data).catch(() => []);
      setBilling(b.data || []);
      setMarketing([...(Array.isArray(m) ? m : []), ...mgrApprov, ...mktgAprov]);
    } catch { toast.error("Failed to load approvals"); }
    finally { setLoading(false); }
  }, [role, canApprove]);

  useEffect(() => { load(); }, [load]);

  const approveBilling = async (doc) => {
    // by_user_id / by_user_name / by_role stamped server-side from JWT
    try { await api.post(`/billing/${doc.doc_id}/approve`, {}); toast.success("Advanced"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Approve failed"); }
  };
  const rejectBilling = async (doc) => {
    const remarks = window.prompt("Reason:", "");
    if (remarks === null) return;
    try { await api.post(`/billing/${doc.doc_id}/reject`, { remarks }); toast.success("Rejected"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Reject failed"); }
  };
  const approveMktg = async (c) => {
    try { await axios.post(`${API}/mktg-campaigns/${c.campaign_id}/approve`, { by_user_id: user.user_id, by_user_name: user.name, by_role: role }); toast.success("Advanced"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Approve failed"); }
  };
  const rejectMktg = async (c) => {
    const remarks = window.prompt("Reason:", "");
    if (remarks === null) return;
    try { await axios.post(`${API}/mktg-campaigns/${c.campaign_id}/reject`, { by_user_id: user.user_id, by_user_name: user.name, by_role: role, remarks }); toast.success("Rejected"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Reject failed"); }
  };

  if (!canApprove) {
    return (
      <div className="min-h-screen bg-slate-50"><Sidebar user={user} logout={logout} />
        <div className="ml-64 p-12"><div className="bg-white p-8 border border-slate-200 rounded">
          <ShieldCheck size={40} className="text-slate-400 mb-3" weight="duotone" />
          <h2 className="text-xl font-bold">Access restricted</h2>
          <p className="text-slate-500 mt-1">Approvals Center is available to Admin, Manager, BDM, Marketing Head, CFO.</p>
        </div></div>
      </div>
    );
  }

  const tabs = [
    { key: "billing", label: "Billing", icon: FileText, count: billing.length },
    { key: "marketing", label: "Marketing", icon: Megaphone, count: marketing.length },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="approvals-center">
      <Sidebar user={user} logout={logout} />
      <div className="ml-64">
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="px-6 py-5 flex items-center gap-3">
            <ShieldCheck size={28} weight="duotone" className="text-[#002FA7]" />
            <div>
              <h1 className="text-xl font-bold">Approvals Center</h1>
              <p className="text-xs text-slate-500">All pending approvals across modules</p>
            </div>
          </div>
          <div className="px-6 flex gap-1">
            {tabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 flex items-center gap-2 ${tab === t.key ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500"}`}>
                  <Icon size={16} /> {t.label}
                  {t.count > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{t.count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {loading ? <p>Loading…</p> :
            tab === "billing" ? (
              <div className="space-y-3">
                {billing.length === 0 && <p className="text-slate-500">No billing approvals pending.</p>}
                {billing.map(d => (
                  <div key={d.doc_id} className="bg-white border border-slate-200 p-4 rounded flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-[#002FA7]">{d.number}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${d.approval_status === "pending_manager" ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>
                          {d.approval_status?.replace(/_/g, " ")}
                        </span>
                        {d.discount_pct > 0 && <span className="text-xs text-amber-600">−{d.discount_pct}% discount</span>}
                      </div>
                      <p className="font-semibold mt-1">{d.customer_name}</p>
                      <p className="text-sm text-slate-500">Total {fmtINR(d.total)} · by {d.created_by_name || "—"}</p>
                    </div>
                    {d.created_by !== user.user_id && (
                      <div className="flex gap-2">
                        <button data-testid={`approve-${d.number}`} onClick={() => approveBilling(d)} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded flex items-center gap-1"><Check size={14} weight="bold" /> Approve</button>
                        <button onClick={() => rejectBilling(d)} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded flex items-center gap-1"><X size={14} weight="bold" /> Reject</button>
                      </div>
                    )}
                    {d.created_by === user.user_id && <p className="text-xs text-slate-400 italic">Your own — cannot approve</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {marketing.length === 0 && <p className="text-slate-500">No marketing approvals pending.</p>}
                {marketing.map(c => (
                  <div key={c.campaign_id} className="bg-white border border-slate-200 p-4 rounded flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-[#002FA7]">{c.campaign_code}</span>
                        <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{c.status?.replace(/_/g, " ")}</span>
                      </div>
                      <p className="font-semibold mt-1">{c.name}</p>
                      <p className="text-sm text-slate-500">Budget {fmtINR(c.budget_planned)} · by {c.owner_name || c.created_by_name || "—"}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => approveMktg(c)} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded flex items-center gap-1"><Check size={14} weight="bold" /> Approve</button>
                      <button onClick={() => rejectMktg(c)} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded flex items-center gap-1"><X size={14} weight="bold" /> Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalsCenter;
